import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchActiveUsersService,
  fetchUsersService,
  fetchMdtRolesService,
  createUserService,
  deleteUserService,
  updateUserService,
  fetchCarePlanMdtUsersService,
} from "../../services/userService";
import { invalidatePagesAfter, setCurrentPage } from "../slices/userSlice";

// Action to fetch all active users other than the care plan creator
export const fetchActiveUsers = createAsyncThunk(
  "users/fetchActiveUsers",
  async (care_plan_id) => {
    const data = await fetchActiveUsersService(care_plan_id); // Pass care_plan_id to the service function
    return data;
  }
);

// Fetch all users list
export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (
    { page, rowsPerPage, searchQuery, forceRefresh = false, ordering = "" },
    { getState }
  ) => {
    const state = getState();
    const { users, userSearchQuery, userRowsPerPage, userTotalRows } =
      state.users;

    // Determine if we need to refresh due to changed parameters
    const needsRefreshDueToParamsChange =
      userRowsPerPage !== rowsPerPage ||
      userSearchQuery.name !== (searchQuery?.name || "") ||
      userSearchQuery.email !== (searchQuery?.email || "");

    // Check if the data for the current page already exists in the state
    if (
      users[page] &&
      !forceRefresh &&
      !ordering &&
      !needsRefreshDueToParamsChange
    ) {
      return {
        data: users[page],
        totalRows: userTotalRows,
        page,
        rowsPerPage,
        searchQuery,
      };
    }

    // Fetch data from the API
    const response = await fetchUsersService(
      page,
      rowsPerPage,
      searchQuery,
      ordering
    );

    // Extract data from the response
    return {
      data: response.results,
      totalRows: response.count,
      page,
      rowsPerPage,
      searchQuery,
    };
  }
);

// Action to search MDT users
export const setUserSearchQuery = (searchQuery) => ({
  type: "users/setSearchQuery",
  payload: searchQuery,
});

// Fetch all the roles in the db
export const fetchMdtRoles = createAsyncThunk("mdtRoles/fetch", async (_) => {
  const response = await fetchMdtRolesService();
  return response;
});

// Create a user
export const createUser = createAsyncThunk(
  "users/addUser",
  async (userData, { dispatch, getState }) => {
    const createdUser = await createUserService(userData);

    // After creation, refetch the first page of users
    const state = getState();
    await dispatch(
      fetchUsers({
        page: 1,
        rowsPerPage: state.users.userRowsPerPage,
        searchQuery: state.users.userSearchQuery,
        forceRefresh: true,
      })
    );

    // Invalidate pages after page 1
    dispatch(invalidatePagesAfter({ fromPage: 1 }));

    return createdUser;
  }
);

// Delete a user
export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (userId, { dispatch, getState }) => {
    // 1. Delete the user from server
    await deleteUserService(userId);

    // 2. Refetch the current page of users
    const state = getState();
    const currentPage = state.users.userCurrentPage;
    const rowsPerPage = state.users.userRowsPerPage;
    const searchQuery = state.users.userSearchQuery;

    // We can see how many items are on this page
    const currentPageData = state.users.users[currentPage] || [];
    const newPageData = currentPageData.filter((u) => u.id !== userId);

    let pageToFetch = currentPage;

    if (newPageData.length === 0 && currentPage > 1) {
      // Decrement the page if it becomes empty
      pageToFetch = currentPage - 1;
    }

    // 1) Set page in the slice: we dispatch an action or do something to store pageToFetch
    dispatch(setCurrentPage(pageToFetch));

    // 2) Now refetch for the new page
    await dispatch(
      fetchUsers({
        page: pageToFetch,
        rowsPerPage,
        searchQuery,
        forceRefresh: true,
      })
    );

    // 3) Invalidate pages after the new page
    dispatch(invalidatePagesAfter({ fromPage: pageToFetch }));

    return userId;
  }
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ userId, formData }, { rejectWithValue }) => {
    try {
      const updatedUser = await updateUserService(userId, formData);
      return updatedUser;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating user.");
    }
  }
);

export const fetchCarePlanMdtUsers = createAsyncThunk(
  "careplan/fetchMdtUsers",
  async (params, { rejectWithValue }) => {
    try {
      const resp = await fetchCarePlanMdtUsersService(params);
      return { ...resp, ...params };
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);
